import type { ApiClient } from "../../api";
import { idempotencyHeaders } from "../../api/idempotency";
import {
  getApiV1DocumentWorkflowImportFailures,
  getApiV1DocumentWorkflowImportFailuresByFailure,
  getApiV1DocumentWorkflowMailboxes,
  getApiV1DocumentWorkflowMailboxesByMailbox,
  getApiV1DocumentWorkflowMailboxesByMailboxSyncState,
  patchApiV1DocumentWorkflowMailboxesByMailbox,
  postApiV1DocumentWorkflowImportFailuresByFailureResolve,
  postApiV1DocumentWorkflowImportFailuresByFailureRetry,
  postApiV1DocumentWorkflowMailboxes,
  postApiV1DocumentWorkflowMailboxesByMailboxSync,
  postApiV1DocumentWorkflowMailboxesByMailboxTestConnection,
  type GetApiV1DocumentWorkflowImportFailuresByFailureSuccess,
  type GetApiV1DocumentWorkflowImportFailuresQuery,
  type GetApiV1DocumentWorkflowImportFailuresSuccess,
  type GetApiV1DocumentWorkflowMailboxesByMailboxSuccess,
  type GetApiV1DocumentWorkflowMailboxesByMailboxSyncStateSuccess,
  type GetApiV1DocumentWorkflowMailboxesQuery,
  type GetApiV1DocumentWorkflowMailboxesSuccess,
  type PatchApiV1DocumentWorkflowMailboxesByMailboxBody,
  type PostApiV1DocumentWorkflowImportFailuresByFailureResolveBody,
  type PostApiV1DocumentWorkflowMailboxesBody,
  type PostApiV1DocumentWorkflowMailboxesByMailboxSyncSuccess,
  type PostApiV1DocumentWorkflowMailboxesByMailboxTestConnectionBody,
  type PostApiV1DocumentWorkflowMailboxesByMailboxTestConnectionSuccess,
} from "../../api/generated";

export type MailboxPage = GetApiV1DocumentWorkflowMailboxesSuccess;
export type MailboxesQuery =
  NonNullable<GetApiV1DocumentWorkflowMailboxesQuery>;
export type MailboxInput = PostApiV1DocumentWorkflowMailboxesBody;
export type MailboxPatch = PatchApiV1DocumentWorkflowMailboxesByMailboxBody;
export type MailboxDetail =
  GetApiV1DocumentWorkflowMailboxesByMailboxSuccess["data"];
export type ConnectionDirection = NonNullable<
  PostApiV1DocumentWorkflowMailboxesByMailboxTestConnectionBody["direction"]
>;
export type ConnectionCheck =
  PostApiV1DocumentWorkflowMailboxesByMailboxTestConnectionSuccess["data"];
export type SyncRun =
  PostApiV1DocumentWorkflowMailboxesByMailboxSyncSuccess["data"];
export type SyncState =
  GetApiV1DocumentWorkflowMailboxesByMailboxSyncStateSuccess["data"];

export type ImportFailure =
  GetApiV1DocumentWorkflowImportFailuresSuccess["data"][number];
export type ImportFailurePage = GetApiV1DocumentWorkflowImportFailuresSuccess;
export type ImportFailureDetail =
  GetApiV1DocumentWorkflowImportFailuresByFailureSuccess["data"];
export type ImportFailuresQuery =
  NonNullable<GetApiV1DocumentWorkflowImportFailuresQuery>;
export type ResolveFailureInput =
  PostApiV1DocumentWorkflowImportFailuresByFailureResolveBody;

/**
 * Почтовые ящики и ошибки импорта. Все операции идут через generated SDK.
 *
 * Секрет ящика (`credential_secret`) только уходит на сервер: ни в ответе
 * Core, ни здесь он не хранится и не возвращается. Ответ содержит лишь признак
 * `has_credential_reference`.
 */
export class MailboxesApi {
  constructor(private readonly client: ApiClient) {}

  list(query: MailboxesQuery = {}): Promise<MailboxPage> {
    return getApiV1DocumentWorkflowMailboxes(this.client, {
      query: { per_page: 100, ...query },
    });
  }

  async get(id: string): Promise<MailboxDetail> {
    return (
      await getApiV1DocumentWorkflowMailboxesByMailbox(this.client, {
        path: { mailbox: id },
      })
    ).data;
  }

  async create(input: MailboxInput, key?: string): Promise<MailboxDetail> {
    return (
      await postApiV1DocumentWorkflowMailboxes(this.client, {
        body: input,
        headers: idempotencyHeaders(key),
      })
    ).data;
  }

  /**
   * Изменение настроек. Удаления ящика в Core нет: отключение — это
   * `enabled: false` в этом же вызове.
   */
  async update(id: string, patch: MailboxPatch): Promise<MailboxDetail> {
    return (
      await patchApiV1DocumentWorkflowMailboxesByMailbox(this.client, {
        path: { mailbox: id },
        body: patch,
      })
    ).data;
  }

  async testConnection(
    id: string,
    direction: ConnectionDirection = "both",
  ): Promise<ConnectionCheck> {
    return (
      await postApiV1DocumentWorkflowMailboxesByMailboxTestConnection(
        this.client,
        { path: { mailbox: id }, body: { direction } },
      )
    ).data;
  }

  /** Ставит ручную синхронизацию в очередь; Core отвечает 202 с записью запуска. */
  async sync(id: string, key?: string): Promise<SyncRun> {
    return (
      await postApiV1DocumentWorkflowMailboxesByMailboxSync(this.client, {
        path: { mailbox: id },
        body: {},
        headers: idempotencyHeaders(key),
      })
    ).data;
  }

  async syncState(id: string): Promise<SyncState> {
    return (
      await getApiV1DocumentWorkflowMailboxesByMailboxSyncState(this.client, {
        path: { mailbox: id },
      })
    ).data;
  }

  failures(query: ImportFailuresQuery = {}): Promise<ImportFailurePage> {
    return getApiV1DocumentWorkflowImportFailures(this.client, { query });
  }

  async failure(id: string): Promise<ImportFailureDetail> {
    return (
      await getApiV1DocumentWorkflowImportFailuresByFailure(this.client, {
        path: { failure: id },
      })
    ).data;
  }

  async retryFailure(
    id: string,
    key?: string,
    reason?: string,
  ): Promise<ImportFailureDetail> {
    return (
      await postApiV1DocumentWorkflowImportFailuresByFailureRetry(this.client, {
        path: { failure: id },
        body: reason === undefined ? {} : { reason },
        headers: idempotencyHeaders(key),
      })
    ).data;
  }

  async resolveFailure(
    id: string,
    input: ResolveFailureInput,
    key?: string,
  ): Promise<ImportFailureDetail> {
    return (
      await postApiV1DocumentWorkflowImportFailuresByFailureResolve(
        this.client,
        {
          path: { failure: id },
          body: input,
          headers: idempotencyHeaders(key),
        },
      )
    ).data;
  }
}
